import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Box, styled } from '@mui/material';
import InvestorAddressList from 'components/new/InvestorAddressList';
import InvestorAddress from 'further-api/domains/lp/investor/investor-address';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import { Country, legalEntityTypeDropdownOptions } from 'further-ui/labels';
import { CalendarDay } from 'further-ui/utils';
import { onboardingAdmin as rules } from 'further-ui/validations/';
import { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import TextField from '../../StyledComponents/TextField';
import DatePicker from '../../StyledComponents/DatePicker';
import CountrySelector from '../../StyledComponents/CountrySelector';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import { LegalEntityType } from 'further-types/legal-entity';
import BasicSelect from '../../StyledComponents/BasicSelect';

const ButtonContainer = styled(Box)({
  textAlign: 'right',
});

type Props = {
  isActive: boolean;
  vehicleId: string;
  vehicleApplicationId: string;
  investor: {
    id: string;
    registeredName: string;
    legalEntityType?: LegalEntityType;
    registrationDate?: CalendarDay;
    registrationState?: string;
    registrationNumber?: string;
    registrationCountry?: Country;
    keyContactPhoneNumber?: string;
    addresses: Array<InvestorAddress>;
  };
  onSave: () => void;
};

const LegalEntity: FC<Props> = ({
  isActive,
  investor,
  vehicleId,
  vehicleApplicationId,
  onSave,
}) => {
  type FormValues = z.infer<typeof rules.SaveLegalEntityDetailsInput>;

  const { saveLegalEntityDetails, isPending } = useVehicleApplication(
    vehicleId,
    vehicleApplicationId,
  );

  const defaultValues = useMemo(() => {
    return {
      investorId: investor.id,
      registeredName: investor.registeredName,
      legalEntityType: investor.legalEntityType,
      registrationDate: investor.registrationDate,
      registrationState: investor.registrationState,
      registrationNumber: investor.registrationNumber,
      registrationCountry: investor.registrationCountry,
      keyContactPhoneNumber: investor.keyContactPhoneNumber,
      addresses: investor.addresses.length
        ? investor.addresses
        : [
            {
              address1: '',
              city: '',
              postcode: '',
              country: undefined,
              hasBeenUsedForThreeYearsOrMore: true,
            },
          ],
    };
  }, [investor]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(rules.SaveLegalEntityDetailsInput),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [isActive, reset, defaultValues]);

  const onSubmit = (values: FormValues) => {
    if (isPending) {
      return;
    }
    saveLegalEntityDetails(
      {
        vehicleId,
        ...values,
      },
      {
        onSuccess: onSave,
      },
    );
  };

  return (
    <Column fullWidth spacing="lg">
      <FormField label="Registered name">
        <Controller
          name="registeredName"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Registered name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Entity type">
        <Controller
          control={control}
          disabled={!isActive}
          name="legalEntityType"
          render={({ field, fieldState }) => (
            <BasicSelect
              {...field}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              placeholder="Select type"
              fullWidth
              size="small"
              options={legalEntityTypeDropdownOptions}
            />
          )}
        />
      </FormField>
      <FormField label="Registration number">
        <Controller
          name="registrationNumber"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Registration number"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Registration date">
        <Controller
          name="registrationDate"
          disabled={!isActive}
          control={control}
          render={({
            field: { value, onChange, name },
            fieldState: { error },
          }) => (
            <DatePicker
              fullWidth
              disabled={!isActive}
              value={value}
              name={name}
              onChange={(value) => {
                onChange(value ?? undefined);
              }}
              error={!!error}
              helperText={error?.message}
              placeholder="Registration date"
            />
          )}
        />
      </FormField>
      <FormField label="Registration state">
        <Controller
          name="registrationState"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Registration state"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Registration country">
        <Controller
          name="registrationCountry"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <CountrySelector
              {...field}
              disabled={!isActive}
              error={error}
              placeholder="Registration country"
            />
          )}
        />
      </FormField>
      <FormField label="Contact phone number">
        <Controller
          name="keyContactPhoneNumber"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Contact phone number"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <InvestorAddressList
        addressFieldName="addresses"
        //@ts-expect-error TODO: Improve the `control` type inside InvestorAddressList
        control={control}
        disabled={!isActive}
        slots={{
          TextField,
          CountrySelector,
        }}
      />
      {isActive ? (
        <ButtonContainer>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            size="large"
            disabled={isPending}
          >
            Save
          </Button>
        </ButtonContainer>
      ) : null}
    </Column>
  );
};

export default LegalEntity;
